import { Component } from '@theme/component';
import { CompareStore } from '@theme/compare-store';

/**
 * ComparePage — renders the comparison table on /pages/compare.
 *
 * Reads product handles from CompareStore, fetches product JSON
 * from the Shopify public API, and builds a side-by-side table.
 *
 * @typedef {object} ComparePageRefs
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} tableWrapper
 * @property {HTMLTableElement} table
 * @property {HTMLTableSectionElement} tableBody
 * @property {HTMLButtonElement} clearAll
 *
 * @extends {Component<ComparePageRefs>}
 */
export class ComparePage extends Component {
  requiredRefs = ['emptyState', 'tableWrapper', 'table', 'tableBody', 'clearAll', 'addAll', 'searchWrapper', 'searchInput', 'searchResults', 'cardsContainer'];

  /** Cache fetched product data in a static Map (shared across instances). */
  static #productCache = new Map();

  /** Cache fetched spec data in a static Map (shared across instances). */
  static #specCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.refs.clearAll.addEventListener('click', this);
    this.refs.addAll.addEventListener('click', this);

    // Search
    this.refs.searchInput.addEventListener('input', this);
    this.refs.searchInput.addEventListener('keydown', this);
    this.refs.searchInput.addEventListener('focus', this);
    this.refs.searchResults.addEventListener('click', this);
    document.addEventListener('click', this); // close dropdown on outside click

    // Listen for store changes (e.g. removal from within the page)
    window.addEventListener('compare:updated', this);

    this.renderTable().catch((err) => {
      console.error('ComparePage initial render failed:', err);
      this.showEmpty();
    });
  }

  disconnectedCallback() {
    this.refs.clearAll?.removeEventListener('click', this);
    this.refs.addAll?.removeEventListener('click', this);
    this.refs.searchInput?.removeEventListener('input', this);
    this.refs.searchInput?.removeEventListener('keydown', this);
    this.refs.searchInput?.removeEventListener('focus', this);
    this.refs.searchResults?.removeEventListener('click', this);
    document.removeEventListener('click', this);
    window.removeEventListener('compare:updated', this);
    this.#clearSearchDebounce?.();
  }

  /**
   * @param {Event} event
   */
  handleEvent(event) {
    switch (event.type) {
      case 'click': {
        // Clear all
        if (event.target === this.refs.clearAll) {
          event.preventDefault();
          CompareStore.clear();
          this.showEmpty();
          return;
        }
        // Add all to cart
        if (event.target === this.refs.addAll) {
          event.preventDefault();
          this.addAllToCart();
          return;
        }
        // Search result "Add" button clicked
        const addBtn = event.target.closest('.compare-page__search-result-add');
        if (addBtn && addBtn.dataset.handle) {
          event.preventDefault();
          event.stopPropagation();
          const handle = addBtn.dataset.handle;
          CompareStore.add(handle);
          addBtn.disabled = true;
          addBtn.textContent = 'Added ✓';
          return;
        }
        // Outside click — close search dropdown
        if (
          this.refs.searchResults &&
          !this.refs.searchResults.hidden &&
          !this.refs.searchWrapper.contains(event.target)
        ) {
          this.#hideSearchResults();
        }
        break;
      }

      case 'input':
        if (event.target === this.refs.searchInput) {
          this.#onSearchInput(event);
        }
        break;

      case 'focus':
        if (event.target === this.refs.searchInput && this.refs.searchInput.value.trim()) {
          this.#onSearchInput(event);
        }
        break;

      case 'keydown':
        if (event.target === this.refs.searchInput) {
          this.#onSearchKeydown(event);
        }
        break;

      case 'compare:updated':
        this.renderTable().catch((err) => {
          console.error('ComparePage renderTable error:', err);
        });
        break;
    }
  }

  /** Main render loop. */
  async renderTable() {
    try {
      const handles = CompareStore.getAll();

      if (handles.length === 0) {
        this.showEmpty();
        return;
      }

      // Create an overall timeout — if data takes >12s, render what we have
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Data fetch timeout')), 12000)
      );

      try {
        // Fetch all product data in parallel (with timeout)
        const products = await Promise.race([
          Promise.all(handles.map((handle) => this.#fetchProductJson(handle))),
          timeout,
        ]);

        const validProducts = (products || []).filter(Boolean);

        if (validProducts.length === 0) {
          this.showEmpty();
          return;
        }

        // Fetch spec data in parallel (with separate timeout)
        const specTimeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Spec fetch timeout')), 8000)
        );

        let specsMap = new Map();
        try {
          specsMap = await Promise.race([
            this.#fetchAllSpecs(validProducts),
            specTimeout,
          ]);
        } catch {
          // Specs timed out — render without them
        }

        this.buildTable(validProducts, specsMap);
      } catch {
        // Overall timeout — try to render with whatever loaded
        // Fetch products individually without timeout
        const products = await Promise.all(
          handles.map((handle) => this.#fetchProductJson(handle))
        );
        const validProducts = products.filter(Boolean);
        if (validProducts.length > 0) {
          this.buildTable(validProducts, new Map());
        } else {
          this.showEmpty();
        }
      }
    } catch (err) {
      // Absolute last resort
      console.error('renderTable fatal error:', err);
      this.showEmpty();
    }
  }

  /** Show empty state. */
  showEmpty() {
    this.refs.emptyState.removeAttribute('hidden');
    this.refs.tableWrapper.setAttribute('hidden', '');
    this.refs.cardsContainer.setAttribute('hidden', '');
  }

  /** Show the table. */
  showTable() {
    this.refs.emptyState.setAttribute('hidden', '');
    this.refs.tableWrapper.removeAttribute('hidden');
    this.refs.cardsContainer.removeAttribute('hidden');
  }

  /**
   * Build the full comparison table.
   * @param {object[]} products
   * @param {Map<string,object>} [specsMap] — spec data keyed by product handle
   */
  buildTable(products, specsMap) {
    const rows = this.#buildTableRows(products, specsMap);

    this.refs.tableBody.innerHTML = '';
    for (const row of rows) {
      this.refs.tableBody.appendChild(row);
    }

    // Build product summary cards
    this.#buildCards(products);

    this.showTable();
  }

  /**
   * Build product summary cards at the top of the compare page.
   * @param {object[]} products
   */
  #buildCards(products) {
    this.refs.cardsContainer.innerHTML = '';

    if (products.length === 0) return;

    // Label column
    const label = document.createElement('div');
    label.className = 'compare-page__cards-label';
    label.innerHTML = '<span>Compare</span>';
    this.refs.cardsContainer.appendChild(label);

    for (const p of products) {
      const card = document.createElement('div');
      card.className = 'compare-page__card';

      // Image
      const imgWrap = document.createElement('div');
      imgWrap.className = 'compare-page__card-image-wrap';

      const img = document.createElement('img');
      img.className = 'compare-page__card-image';
      img.src = p.featured_image || p.image?.src || '';
      img.alt = p.title || '';
      img.loading = 'lazy';
      img.width = 200;
      img.height = 200;
      imgWrap.appendChild(img);

      // Badge
      const badgeTag = this.#badgeFromTags(p);
      if (badgeTag) {
        const badgeEl = document.createElement('span');
        badgeEl.className = 'compare-page__card-badge';
        badgeEl.textContent = badgeTag;
        imgWrap.appendChild(badgeEl);
      }

      card.appendChild(imgWrap);

      // Body
      const body = document.createElement('div');
      body.className = 'compare-page__card-body';

      // Title
      const title = document.createElement('div');
      title.className = 'compare-page__card-title';
      title.textContent = p.title || '';
      body.appendChild(title);

      // Price
      const price = document.createElement('div');
      price.className = 'compare-page__card-price';
      price.textContent = this.#formatPrice(p.price);
      body.appendChild(price);

      // Compare-at price
      if (p.compare_at_price && parseFloat(p.compare_at_price) > 0) {
        const cmpPrice = document.createElement('span');
        cmpPrice.className = 'compare-page__card-compare-price';
        cmpPrice.textContent = this.#formatPrice(p.compare_at_price);
        price.appendChild(cmpPrice);
      }

      // Availability
      const avail = document.createElement('div');
      avail.className = `compare-page__card-availability compare-page__card-availability--${p.available ? 'in-stock' : 'sold-out'}`;
      avail.textContent = p.available ? 'In Stock' : 'Sold Out';
      body.appendChild(avail);

      // Actions
      const actions = document.createElement('div');
      actions.className = 'compare-page__card-actions';

      // Add to cart button
      if (p.available && p.variants?.[0]) {
        const addBtn = document.createElement('button');
        addBtn.type = 'button';
        addBtn.className = 'compare-page__card-add-btn';
        addBtn.textContent = 'Add to cart';
        addBtn.dataset.variantId = String(p.variants[0].id);
        addBtn.addEventListener('click', async () => {
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: Number(p.variants[0].id), quantity: 1 }),
            });
            addBtn.textContent = 'Added ✓';
          } catch {
            addBtn.textContent = 'Error';
          }
        });
        actions.appendChild(addBtn);
      }

      // Remove button
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'compare-page__card-remove-btn';
      removeBtn.textContent = 'Remove';
      removeBtn.addEventListener('click', () => {
        CompareStore.remove(p.handle);
      });
      actions.appendChild(removeBtn);

      body.appendChild(actions);
      card.appendChild(body);
      this.refs.cardsContainer.appendChild(card);
    }
  }

  /**
   * Generate all rows for the comparison table.
   * @param {object[]} products
   * @param {Map<string,object>} [specsMap]
   * @returns {HTMLTableRowElement[]}
   */
  #buildTableRows(products, specsMap) {
    const rows = [];

    // ── Product Image row ──
    rows.push(this.#sectionDividerRow('Product'));

    const imageRow = document.createElement('tr');
    imageRow.appendChild(this.#labelCell(''));
    for (const p of products) {
      imageRow.appendChild(this.#imageCell(p));
    }
    rows.push(imageRow);

    // ── Price row ──
    const priceRow = document.createElement('tr');
    priceRow.appendChild(this.#labelCell('Price'));
    for (const p of products) {
      priceRow.appendChild(this.#priceCell(p));
    }
    rows.push(priceRow);

    // ── Availability row ──
    const availRow = document.createElement('tr');
    availRow.appendChild(this.#labelCell('Availability'));
    for (const p of products) {
      availRow.appendChild(this.#availCell(p));
    }
    rows.push(availRow);

    // ── Vendor row ──
    if (products.some((p) => p.vendor)) {
      const vendorRow = document.createElement('tr');
      vendorRow.appendChild(this.#labelCell('Brand'));
      for (const p of products) {
        vendorRow.appendChild(this.#vendorCell(p));
      }
      rows.push(vendorRow);
    }

    // ── Description row ──
    if (products.some((p) => p.description && p.description.trim())) {
      rows.push(this.#sectionDividerRow('Details'));
      const descRow = document.createElement('tr');
      descRow.appendChild(this.#labelCell('Description'));
      for (const p of products) {
        descRow.appendChild(this.#descCell(p));
      }
      rows.push(descRow);
    }

    // ── Type row ──
    if (products.some((p) => p.type)) {
      const typeRow = document.createElement('tr');
      typeRow.appendChild(this.#labelCell('Category'));
      for (const p of products) {
        typeRow.appendChild(this.#textCell(p.type || '—'));
      }
      rows.push(typeRow);
    }

    // ── Spec comparison rows (if spec data available) ──
    if (specsMap) {
      rows.push(...this.#buildSpecRows(products, specsMap));
    }

    // ── Add to cart / Remove row ──
    rows.push(this.#sectionDividerRow('Actions'));
    const actionsRow = document.createElement('tr');
    actionsRow.appendChild(this.#labelCell(''));
    for (const p of products) {
      actionsRow.appendChild(this.#actionsCell(p));
    }
    rows.push(actionsRow);

    return rows;
  }

  // ─── Cell builders ───

  /** @param {string} label */
  #sectionDividerRow(label) {
    const tr = document.createElement('tr');
    tr.className = 'compare-page__section-divider';
    const td = document.createElement('td');
    td.setAttribute('colspan', '10');
    td.innerHTML = `<span>${this.#esc(label)}</span>`;
    tr.appendChild(td);
    return tr;
  }

  /** @param {string} text */
  #labelCell(text) {
    const th = document.createElement('th');
    th.textContent = text;
    return th;
  }

  /**
   * Image cell.
   * @param {object} p
   * @returns {HTMLTableCellElement}
   */
  #imageCell(p) {
    const td = document.createElement('td');
    const imgObj = p.image || p.images?.[0];
    if (imgObj && imgObj.src) {
      const img = document.createElement('img');
      img.className = 'compare-page__product-image';
      // Append a size param for a 280px-wide image – faster to load than full-res
      img.src = imgObj.src.replace(/(\.[a-z]+)(\?|$)/i, '_280x$1$2');
      img.alt = p.title || '';
      img.loading = 'lazy';
      img.width = 280;
      img.height = 280;
      td.appendChild(img);
    }
    // Badge
    const badge = this.#badgeFromTags(p);
    if (badge) {
      const span = document.createElement('span');
      span.className = 'compare-page__product-badge';
      span.textContent = badge;
      td.appendChild(span);
    }
    // Title link — eliminates the need for a separate "Model" row
    if (p.title || p.handle) {
      const link = document.createElement('a');
      link.className = 'compare-page__product-title';
      link.href = `/products/${p.handle}`;
      link.textContent = p.title || '';
      td.appendChild(link);
    }
    return td;
  }

  /**
   * Price cell.
   * @param {object} p
   * @returns {HTMLTableCellElement}
   */
  #priceCell(p) {
    const td = document.createElement('td');
    const price = document.createElement('span');
    price.className = 'compare-page__product-price';
    price.textContent = this.#formatPrice(p.price);
    td.appendChild(price);

    if (p.compare_at_price && parseFloat(p.compare_at_price) > 0) {
      const compare = document.createElement('span');
      compare.className = 'compare-page__product-compare-price';
      compare.textContent = this.#formatPrice(p.compare_at_price);
      td.appendChild(compare);
    }
    return td;
  }

  /**
   * Availability cell.
   * @param {object} p
   * @returns {HTMLTableCellElement}
   */
  #availCell(p) {
    const td = document.createElement('td');
    if (p.available) {
      td.textContent = 'In Stock';
    } else {
      const soldOut = document.createElement('span');
      soldOut.className = 'compare-page__sold-out';
      soldOut.textContent = 'Sold Out';
      td.appendChild(soldOut);
    }
    return td;
  }

  /**
   * Vendor cell.
   * @param {object} p
   * @returns {HTMLTableCellElement}
   */
  #vendorCell(p) {
    const td = document.createElement('td');
    const span = document.createElement('span');
    span.className = 'compare-page__product-vendor';
    span.textContent = p.vendor || '—';
    td.appendChild(span);
    return td;
  }

  /**
   * Description cell (truncated).
   * @param {object} p
   * @returns {HTMLTableCellElement}
   */
  #descCell(p) {
    const td = document.createElement('td');
    const div = document.createElement('div');
    div.className = 'compare-page__product-desc';
    // Strip HTML tags for the description
    const text = (p.description || '').replace(/<[^>]*>/g, '');
    div.textContent = text.length > 300 ? text.slice(0, 300) + '…' : text;
    td.appendChild(div);
    return td;
  }

  /**
   * Generic text cell.
   * @param {string} text
   * @returns {HTMLTableCellElement}
   */
  #textCell(text) {
    const td = document.createElement('td');
    td.textContent = text;
    return td;
  }

  /**
   * Actions cell: add to cart + remove buttons.
   * @param {object} p
   * @returns {HTMLTableCellElement}
   */
  #actionsCell(p) {
    const td = document.createElement('td');
    const wrapper = document.createElement('div');
    wrapper.className = 'compare-page__product-actions';

    if (p.available && p.variants?.[0]) {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/cart/add';
      form.target = '_blank';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'id';
      input.value = String(p.variants[0].id);
      form.appendChild(input);

      const btn = document.createElement('button');
      btn.type = 'submit';
      btn.className = 'button';
      btn.textContent = 'Add to cart';
      form.appendChild(btn);

      wrapper.appendChild(form);
    }

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'compare-page__remove-btn';
    removeBtn.textContent = 'Remove';
    removeBtn.dataset.handle = p.handle;
    removeBtn.addEventListener('click', () => {
      CompareStore.remove(p.handle);
    });
    wrapper.appendChild(removeBtn);

    td.appendChild(wrapper);
    return td;
  }

  // ─── Spec data ───

  /**
   * Fetch spec data for all valid products in parallel.
   * @param {object[]} products
   * @returns {Promise<Map<string,object>>}
   */
  async #fetchAllSpecs(products) {
    const entries = await Promise.all(
      products.map(async (p) => {
        const data = await this.#fetchProductSpecs(p.handle);
        return [p.handle, data];
      })
    );
    return new Map(entries);
  }

  /**
   * Fetch spec metafield data via the ?view=specs endpoint.
   * @param {string} handle
   * @returns {Promise<object|null>}
   */
  async #fetchProductSpecs(handle) {
    if (ComparePage.#specCache.has(handle)) {
      return ComparePage.#specCache.get(handle);
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`/products/${handle}?view=specs`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      clearTimeout(timeout);
      if (!response.ok) return null;
      const data = await response.json();
      ComparePage.#specCache.set(handle, data);
      return data;
    } catch {
      return null;
    }
  }

  /**
   * Build spec comparison rows (highlights, electrical, physical).
   * @param {object[]} products
   * @param {Map<string,object>} specsMap
   * @returns {HTMLTableRowElement[]}
   */
  #buildSpecRows(products, specsMap) {
    const rows = [];

    // ── Highlights section ──
    rows.push(...this.#buildHighlightsRows(products, specsMap));

    // ── Electrical specs matrix (from metafields) ──
    rows.push(...this.#buildSpecMatrixRows(products, specsMap, 'Electrical specs', 'electrical_specs'));

    // ── Physical specs matrix (from metafields) ──
    rows.push(...this.#buildSpecMatrixRows(products, specsMap, 'Physical specs', 'physical_specs'));

    // ── Derived specs (always available from product JSON) ──
    rows.push(...this.#buildDerivedSpecRows(products));

    return rows;
  }

  /**
   * Parse product description HTML for structured spec content.
   *
   * Sources (in priority order):
   *   1. HTML tables  — <table><tr><td>Label</td><td>Value</td></tr>
   *   2. List items with "Label: Value" pattern  — <li>Display: 27-inch IPS</li>
   *
   * Collects shared labels across products and returns them as
   * [{ label, values: Map<handle, string> }, ...].
   *
   * @param {object[]} products
   * @returns {Array<{label: string, values: Map<string,string>}>}
   */
  #collectDescriptionSpecs(products) {
    const productSpecs = new Map(); // handle → Map<label, value>

    for (const p of products) {
      const specMap = new Map();
      if (p.body_html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(p.body_html, 'text/html');

        // ── Source 1: HTML tables (<tr><td>Label</td><td>Value</td>) ──
        const tableRows = doc.querySelectorAll('table tr');
        for (const row of tableRows) {
          const cells = row.querySelectorAll('td');
          if (cells.length >= 2) {
            const label = cells[0].textContent.trim();
            const value = cells[1].textContent.trim();
            if (label && value) {
              specMap.set(label, value);
            }
          }
        }

        // ── Source 2: <li> items with "Label: Value" pattern ──
        // Matches patterns like "Display: 27-inch IPS", "Refresh rate: 165Hz"
        // but NOT long marketing sentences (skip items >100 chars or without colon)
        const listItems = doc.querySelectorAll('li');
        for (const li of listItems) {
          const text = li.textContent.trim();
          if (!text) continue;
          // Only process items that have a clear "Label: Value" pattern
          const colonIdx = text.indexOf(':');
          if (colonIdx > 0 && colonIdx < 60) { // label must be short (under 60 chars)
            const label = text.slice(0, colonIdx).trim();
            const value = text.slice(colonIdx + 1).trim();
            // Skip marketing copy: labels with "Enjoy", "Designed", "Featuring", etc.
            // Skip very long values (>120 chars = marketing paragraph)
            if (label && value && value.length <= 120
                && !/^(Enjoy|Designed|Featuring|Experience|Perfect|Ideal|With|Born|Be|Take|Get|Step|Upgrade|Protect|Meet|One|Feel)/i.test(label)
                && !/^(Enjoy|Designed|Featuring|Experience|Perfect|Ideal|Be)$/i.test(value)
            ) {
              // Don't overwrite a table spec with a less-precise li spec
              if (!specMap.has(label)) {
                specMap.set(label, value);
              }
            }
          }
        }
      }
      productSpecs.set(p.handle, specMap);
    }

    // Collect all unique labels across all products
    const allLabels = new Set();
    for (const specMap of productSpecs.values()) {
      for (const label of specMap.keys()) {
        allLabels.add(label);
      }
    }

    // Build result: shared labels only (≥2 products have it OR it's the only product)
    const result = [];
    for (const label of allLabels) {
      const handlesWithLabel = products.filter((p) => productSpecs.get(p.handle)?.has(label));
      // Show label if at least 2 products share it, or if only 1 product in comparison
      if (handlesWithLabel.length >= 2 || products.length === 1) {
        const values = new Map();
        for (const p of products) {
          values.set(p.handle, productSpecs.get(p.handle)?.get(label) || null);
        }
        result.push({ label, values });
      }
    }

    return result;
  }

  /**
   * Build spec rows from product JSON data that's always available
   * (type, tags, dates) — shown even when metafield specs are empty.
   * @param {object[]} products
   * @returns {HTMLTableRowElement[]}
   */
  #buildDerivedSpecRows(products) {
    const rows = [];
    let hasAny = false;

    // ── Tags row (only if at least one product has tags) ──
    // Note: Shopify API returns tags as a comma-separated STRING, not an array.
    if (products.some((p) => p.tags && p.tags.length > 0)) {
      hasAny = true;
      const tr = document.createElement('tr');
      tr.appendChild(this.#labelCell('Tags'));
      for (const p of products) {
        const td = document.createElement('td');
        const tagsArr = typeof p.tags === 'string'
          ? p.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : (Array.isArray(p.tags) ? p.tags : []);
        const tags = tagsArr.filter(
          (t) => !['badge', 'new', 'sale', 'featured'].some((s) => t.toLowerCase().includes(s))
        );
        td.textContent = tags.length > 0 ? tags.slice(0, 5).join(', ') : '—';
        tr.appendChild(td);
      }
      rows.push(tr);
    }

    // ── Product type row (always available) ──
    hasAny = true;
    const typeRow = document.createElement('tr');
    typeRow.className = 'compare-page__spec-row';
    typeRow.appendChild(this.#labelCell('Category'));
    for (const p of products) {
      const td = document.createElement('td');
      td.className = 'compare-page__spec-value';
      td.textContent = p.product_type || '—';
      typeRow.appendChild(td);
    }
    rows.push(typeRow);

    // ── Specs from product description (HTML tables + <li> patterns) ──
    // Many products store structured specs as HTML tables in body_html
    // (e.g. Amazon-format <table class="a-normal"> with label/value rows)
    // or as <li>"Label: Value"</li> items in feature lists.
    const descSpecs = this.#collectDescriptionSpecs(products);
    for (const { label, values } of descSpecs) {
      hasAny = true;
      const tr = document.createElement('tr');
      tr.className = 'compare-page__spec-row';
      tr.appendChild(this.#labelCell(label));
      for (const p of products) {
        const td = document.createElement('td');
        td.className = 'compare-page__spec-value';
        td.textContent = values.get(p.handle) || '—';
        tr.appendChild(td);
      }
      rows.push(tr);
    }

    // ── Published date row ──
    if (products.some((p) => p.published_at || p.created_at)) {
      hasAny = true;
      const tr = document.createElement('tr');
      tr.appendChild(this.#labelCell('Published'));
      for (const p of products) {
        const td = document.createElement('td');
        const dateStr = p.published_at || p.created_at;
        if (dateStr) {
          try {
            td.textContent = new Date(dateStr).toLocaleDateString('en-EG', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
          } catch {
            td.textContent = dateStr;
          }
        } else {
          td.textContent = '—';
        }
        tr.appendChild(td);
      }
      rows.push(tr);
    }

    if (hasAny) {
      rows.unshift(this.#sectionDividerRow('Product info'));
    }

    return rows;
  }

  /**
   * Build highlights rows. Since highlights may have different labels per product,
   * we show each product's highlights as a compact list in their column cell.
   * @param {object[]} products
   * @param {Map<string,object>} specsMap
   * @returns {HTMLTableRowElement[]}
   */
  #buildHighlightsRows(products, specsMap) {
    // Check if any product has highlights data
    const hasAny = products.some((p) => {
      const highlights = specsMap.get(p.handle)?.highlights;
      return Array.isArray(highlights) && highlights.length > 0;
    });
    if (!hasAny) return [];

    const rows = [];
    rows.push(this.#sectionDividerRow('Highlights'));

    const tr = document.createElement('tr');
    tr.appendChild(this.#labelCell('Highlights'));
    for (const p of products) {
      const td = document.createElement('td');
      td.className = 'compare-page__highlights-cell';
      const highlights = specsMap.get(p.handle)?.highlights;
      if (Array.isArray(highlights) && highlights.length > 0) {
        const list = document.createElement('ul');
        list.className = 'compare-page__highlights-list';
        for (const h of highlights) {
          if (!h || (!h.label && !h.value)) continue;
          const li = document.createElement('li');
          li.className = 'compare-page__highlights-item';
          if (h.label) {
            const labelSpan = document.createElement('span');
            labelSpan.className = 'compare-page__highlights-label';
            labelSpan.textContent = h.label + ': ';
            li.appendChild(labelSpan);
            const valSpan = document.createElement('span');
            valSpan.className = 'compare-page__highlights-value';
            valSpan.textContent = h.value || '—';
            li.appendChild(valSpan);
          } else {
            li.textContent = h.value || '—';
          }
          list.appendChild(li);
        }
        td.appendChild(list);
      } else {
        td.textContent = '—';
      }
      tr.appendChild(td);
    }
    rows.push(tr);
    return rows;
  }

  /**
   * Build a spec matrix section (e.g. electrical specs, physical specs).
   * Collects all unique labels across all products and renders a row per label.
   * @param {object[]} products
   * @param {Map<string,object>} specsMap
   * @param {string} sectionName — Section divider label
   * @param {string} specKey — 'electrical_specs' or 'physical_specs'
   * @returns {HTMLTableRowElement[]}
   */
  #buildSpecMatrixRows(products, specsMap, sectionName, specKey) {
    // Collect specs per product
    const productSpecs = new Map(); // handle -> array of {label, value}
    const allLabels = new Set();

    for (const p of products) {
      const specs = specsMap.get(p.handle)?.[specKey];
      const arr = Array.isArray(specs) ? specs : [];
      productSpecs.set(p.handle, arr);
      for (const spec of arr) {
        if (spec?.label) {
          allLabels.add(spec.label);
        }
      }
    }

    if (allLabels.size === 0) return [];

    const rows = [];
    rows.push(this.#sectionDividerRow(sectionName));

    for (const label of allLabels) {
      const tr = document.createElement('tr');
      tr.className = 'compare-page__spec-row';
      tr.appendChild(this.#labelCell(label));
      for (const p of products) {
        const specs = productSpecs.get(p.handle) || [];
        const match = specs.find((s) => s.label === label);
        const td = document.createElement('td');
        td.className = 'compare-page__spec-value';
        td.textContent = match ? (match.value || '—') : '—';
        tr.appendChild(td);
      }
      rows.push(tr);
    }

    return rows;
  }

  // ─── Product data fetching ───

  /**
   * Fetch product JSON from Shopify's public API and normalize it
   * (Shopify individual product API doesn't expose price/available at the top level).
   * @param {string} handle
   * @returns {Promise<object|null>}
   */
  async #fetchProductJson(handle) {
    if (ComparePage.#productCache.has(handle)) {
      return ComparePage.#productCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.json`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const data = await response.json();
      const product = this.#normalizeProduct(data.product || data);
      ComparePage.#productCache.set(handle, product);
      return product;
    } catch {
      return null;
    }
  }

  /**
   * Normalize product data from the Shopify individual product API.
   *
   * Shopify's /products/{handle}.json does NOT return `price`, `price_min`,
   * `price_max`, or `available` at the product level. We must read from
   * `variants[0]` and derive availability from inventory rules.
   *
   * @param {object|null} p
   * @returns {object|null}
   */
  #normalizeProduct(p) {
    if (!p) return null;
    const v = p.variants?.[0];

    // The public /products/{handle}.json API often omits inventory_quantity
    // and inventory_policy entirely. We default to available unless we can
    // definitively prove the product is sold out.
    let available = true;
    if (v) {
      const isTracked = v.inventory_management !== null && v.inventory_management !== '';
      if (isTracked && v.inventory_quantity !== null && v.inventory_quantity !== undefined) {
        // Only when we have actual inventory data can we mark as sold out
        const hasStock = Number(v.inventory_quantity) > 0;
        const canOversell = v.inventory_policy === 'continue';
        if (!hasStock && !canOversell) {
          available = false;
        }
      }
      // If inventory_quantity is missing from API, assume available
    }

    return {
      ...p,
      price: v?.price || '0',
      compare_at_price: v?.compare_at_price || null,
      available,
      featured_image: p.featured_image || p.image?.src || '',
    };
  }

  // ─── Add all to cart ───

  /**
   * Add all products in the comparison to the cart via the Shopify AJAX API.
   * Shows loading state on the button, then success/error feedback.
   */
  async addAllToCart() {
    const handles = CompareStore.getAll();
    if (handles.length === 0) return;

    const btn = this.refs.addAll;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      // Fetch all product data
      const products = await Promise.all(
        handles.map((handle) => this.#fetchProductJson(handle))
      );
      const valid = products.filter(Boolean);

      // Build items array: take the first variant ID for each
      const items = [];
      for (const p of valid) {
        const vid = p.variants?.[0]?.id;
        if (vid) {
          items.push({ id: vid, quantity: 1 });
        }
      }

      if (items.length === 0) {
        btn.textContent = originalText;
        btn.disabled = false;
        return;
      }

      // POST to cart/add.js
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Success — redirect to cart or show brief success state
      btn.textContent = 'Added!';
      setTimeout(() => {
        // Optionally navigate to cart
        window.location.href = '/cart';
      }, 600);
    } catch (err) {
      console.error('Failed to add all to cart:', err);
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }

  // ─── Search-to-add ───

  /** @type {number|null} */
  #searchDebounce = null;

  /** Debounced input handler. */
  #onSearchInput() {
    this.#clearSearchDebounce();
    const q = this.refs.searchInput.value.trim();

    if (q.length < 2) {
      this.#hideSearchResults();
      return;
    }

    this.#searchDebounce = setTimeout(() => {
      this.#fetchSearchResults(q);
    }, 250);
  }

  /** Clear the debounce timer. */
  #clearSearchDebounce() {
    if (this.#searchDebounce !== null) {
      clearTimeout(this.#searchDebounce);
      this.#searchDebounce = null;
    }
  }

  /**
   * Fetch product suggestions from Shopify's suggest API.
   * @param {string} query
   */
  async #fetchSearchResults(query) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&limit=6`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const products = data?.resources?.results?.products || [];
      this.#renderSearchResults(products);
    } catch {
      this.#renderSearchResults([]);
    }
  }

  /**
   * Render the search results dropdown.
   * @param {Array<{title: string, image: string, url: string, handle?: string}>} products
   */
  #renderSearchResults(products) {
    this.refs.searchResults.innerHTML = '';

    if (products.length === 0) {
      const li = document.createElement('li');
      li.className = 'compare-page__search-no-results';
      li.textContent = 'No products found';
      this.refs.searchResults.appendChild(li);
      this.refs.searchResults.hidden = false;
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
      return;
    }

    const alreadySelected = CompareStore.getAll();

    for (const p of products) {
      // Extract handle from url
      const handle = p.handle || p.url?.replace(/^\/products\//, '') || '';
      if (!handle) continue;

      const li = document.createElement('li');
      li.className = 'compare-page__search-result';
      li.setAttribute('role', 'option');

      // Image
      const img = document.createElement('img');
      img.className = 'compare-page__search-result-img';
      img.src = p.image || '';
      img.alt = p.title || '';
      img.loading = 'lazy';
      img.width = 36;
      img.height = 36;
      li.appendChild(img);

      // Info
      const info = document.createElement('div');
      info.className = 'compare-page__search-result-info';
      const title = document.createElement('span');
      title.className = 'compare-page__search-result-title';
      title.textContent = p.title || '';
      info.appendChild(title);
      li.appendChild(info);

      // Add button
      const addBtn = document.createElement('button');
      addBtn.className = 'compare-page__search-result-add';
      addBtn.dataset.handle = handle;
      addBtn.type = 'button';

      if (alreadySelected.includes(handle)) {
        addBtn.disabled = true;
        addBtn.textContent = 'Added ✓';
      } else if (alreadySelected.length >= 4) {
        addBtn.disabled = true;
        addBtn.textContent = 'Max (4)';
      } else {
        addBtn.textContent = '+ Add';
      }

      li.appendChild(addBtn);
      this.refs.searchResults.appendChild(li);
    }

    this.refs.searchResults.hidden = false;
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  }

  /** Hide the search results dropdown. */
  #hideSearchResults() {
    this.refs.searchResults.hidden = true;
    this.refs.searchResults.innerHTML = '';
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
  }

  /** Handle keyboard navigation in the search dropdown. */
  #onSearchKeydown(event) {
    if (this.refs.searchResults.hidden) return;

    const items = this.refs.searchResults.querySelectorAll('.compare-page__search-result');
    if (items.length === 0) return;

    const current = this.refs.searchResults.querySelector('[aria-selected="true"]');
    let index = current ? Array.from(items).indexOf(current) : -1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        index = Math.min(index + 1, items.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        index = Math.max(index - 1, 0);
        break;
      case 'Enter':
        event.preventDefault();
        if (current) {
          const addBtn = current.querySelector('.compare-page__search-result-add');
          if (addBtn && !addBtn.disabled) {
            addBtn.click();
          }
        }
        return;
      case 'Escape':
        event.preventDefault();
        this.#hideSearchResults();
        this.refs.searchInput.blur();
        return;
      default:
        return;
    }

    items.forEach((el) => el.removeAttribute('aria-selected'));
    if (index >= 0) {
      items[index].setAttribute('aria-selected', 'true');
      items[index].scrollIntoView({ block: 'nearest' });
    }
  }

  // ─── Utility ───

  /**
   * Format a price string with currency symbol.
   * Falls back to raw value if formatting fails.
   * @param {string|number} price
   * @returns {string}
   */
  #formatPrice(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return price || '—';

    try {
      return new Intl.NumberFormat('en-EG', {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `${num.toFixed(2)} EGP`;
    }
  }

  /**
   * Derive a badge label from product tags.
   * @param {object} p
   * @returns {string|null}
   */
  #badgeFromTags(p) {
    const tags = typeof p.tags === 'string'
      ? p.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : (Array.isArray(p.tags) ? p.tags : []);
    if (tags.length === 0) return null;
    const badgeTag = tags.find(
      (t) =>
        t.toLowerCase().includes('badge') ||
        t.toLowerCase().includes('new') ||
        t.toLowerCase().includes('sale') ||
        t.toLowerCase().includes('featured')
    );
    return badgeTag || null;
  }

  /**
   * Escape HTML entities in a string.
   * @param {string} str
   * @returns {string}
   */
  #esc(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

}

if (!customElements.get('compare-page')) {
  customElements.define('compare-page', ComparePage);
}
